/**
 * /app/merchant/config — Configuración del nicho Merchant.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { DashboardHeader } from "@/components/DashboardHeader";
import { ClinicalPageShell } from "@/components/clinical";
import { PageLoader } from "@/components/PageLoader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import type { MerchantTenantConfig, GarmentType, Zone, WorkType } from "@/lib/db-service/merchant";
import { MerchantConfigTour } from "@/components/ProductTour";

export const Route = createFileRoute("/app/merchant/config")({
  component: MerchantConfigPage,
  head: () => ({ meta: [{ title: "Merchant Config — Structura Market" }] }),
});

function ObjectTypeAccordion({
  garmentType,
  zones,
  enabledZones,
  onToggleZone,
}: {
  garmentType: GarmentType;
  zones: Zone[];
  enabledZones: string[];
  onToggleZone: (id: string) => void;
}) {
  const [open, setOpen] = useState(true);
  const gtZones = zones.filter((z) => z.garmentTypeId === garmentType.id);
  if (!gtZones.length) return null;

  const enabledCount = gtZones.filter((z) => enabledZones.includes(z.id)).length;
  const allEnabled = enabledCount === gtZones.length;

  function toggleAll() {
    if (allEnabled) {
      gtZones.forEach((z) => { if (enabledZones.includes(z.id)) onToggleZone(z.id); });
    } else {
      gtZones.forEach((z) => { if (!enabledZones.includes(z.id)) onToggleZone(z.id); });
    }
  }

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      {/* Header del acordeón */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-muted/40 hover:bg-muted/70 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          <span className="font-medium text-sm">{garmentType.name}</span>
          <span className="text-xs text-muted-foreground">
            {enabledCount}/{gtZones.length} zonas activas
          </span>
        </div>
        {/* Toggle todo — detener propagación para no abrir/cerrar el acordeón */}
        <div
          className="flex items-center gap-2"
          onClick={(e) => { e.stopPropagation(); toggleAll(); }}
        >
          <span className="text-xs text-muted-foreground">{allEnabled ? "Deshabilitar todas" : "Habilitar todas"}</span>
          <Switch checked={allEnabled} onCheckedChange={toggleAll} />
        </div>
      </button>

      {/* Zonas */}
      {open && (
        <div className="divide-y divide-border/50">
          {gtZones.map((zone) => (
            <div key={zone.id} className="flex items-center justify-between px-6 py-2.5">
              <Label htmlFor={`zone-${zone.id}`} className="text-sm cursor-pointer text-muted-foreground">
                {zone.name}
              </Label>
              <Switch
                id={`zone-${zone.id}`}
                checked={enabledZones.includes(zone.id)}
                onCheckedChange={() => onToggleZone(zone.id)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MerchantConfigPage() {
  const tid = useWorkspaceTenantId();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [garmentTypes, setGarmentTypes] = useState<GarmentType[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [workTypes, setWorkTypes] = useState<WorkType[]>([]);

  const [enabledZones, setEnabledZones] = useState<string[]>([]);
  const [enabledWorkTypes, setEnabledWorkTypes] = useState<string[]>([]);
  const [quoteValidityHours, setQuoteValidityHours] = useState(48);
  const [leadTimeDaysMin, setLeadTimeDaysMin] = useState(3);
  const [leadTimeDaysMax, setLeadTimeDaysMax] = useState(7);

  const load = useCallback(async () => {
    if (!tid) return;
    setLoading(true);
    try {
      const [cfg, gt, z, wt] = await Promise.all([
        apiGetJson<MerchantTenantConfig | null>("/api/merchant/config", tid),
        apiGetJson<GarmentType[]>("/api/merchant/garment-types", tid),
        apiGetJson<Zone[]>("/api/merchant/zones", tid),
        apiGetJson<WorkType[]>("/api/merchant/work-types", tid),
      ]);
      setGarmentTypes(gt);
      setZones(z);
      setWorkTypes(wt);
      if (cfg) {
        setEnabledZones(cfg.enabledZones);
        setEnabledWorkTypes(cfg.enabledWorkTypes);
        setQuoteValidityHours(cfg.quoteValidityHours);
        setLeadTimeDaysMin(cfg.leadTimeDaysMin);
        setLeadTimeDaysMax(cfg.leadTimeDaysMax);
      } else {
        setEnabledZones(z.map((zone) => zone.id));
        setEnabledWorkTypes(wt.map((w) => w.id));
      }
    } catch {
      toast.error("Error al cargar la configuración");
    } finally {
      setLoading(false);
    }
  }, [tid]);

  useEffect(() => { void load(); }, [load]);

  async function handleSave() {
    if (!tid) return;
    setSaving(true);
    try {
      await apiSendJson("/api/merchant/config", "PATCH", {
        enabledZones,
        enabledWorkTypes,
        quoteValidityHours,
        leadTimeDaysMin,
        leadTimeDaysMax,
      }, tid);
      toast.success("Configuración guardada");
    } catch {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  }

  function toggleZone(id: string) {
    setEnabledZones((prev) => prev.includes(id) ? prev.filter((z) => z !== id) : [...prev, id]);
  }

  function toggleWorkType(id: string) {
    setEnabledWorkTypes((prev) => prev.includes(id) ? prev.filter((w) => w !== id) : [...prev, id]);
  }

  if (!tid || loading) return <PageLoader />;

  return (
    <div>
      <DashboardHeader
        title="Configuración Merchant"
        subtitle="Tipos de trabajo, objetos personalizables y parámetros de cotización"
        layout="fixed-title"
      />
      <ClinicalPageShell offset="none">
      <MerchantConfigTour />
      

      <div className="max-w-2xl space-y-8 mt-6">

        {/* Tipos de trabajo */}
        <section data-tour="merchant-config-work-types">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            Tipos de trabajo
          </h2>
          <div className="border border-border rounded-lg divide-y divide-border/50">
            {workTypes.map((wt) => (
              <div key={wt.id} className="flex items-center justify-between px-4 py-3">
                <Label htmlFor={`wt-${wt.id}`} className="text-sm font-medium cursor-pointer">
                  {wt.name}
                </Label>
                <Switch
                  id={`wt-${wt.id}`}
                  checked={enabledWorkTypes.includes(wt.id)}
                  onCheckedChange={() => toggleWorkType(wt.id)}
                />
              </div>
            ))}
          </div>
        </section>

        {/* Zonas por tipo de objeto — acordeón */}
        <section data-tour="merchant-config-objects">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            Objetos y zonas personalizables
          </h2>
          <div className="space-y-2">
            {garmentTypes.map((gt) => (
              <ObjectTypeAccordion
                key={gt.id}
                garmentType={gt}
                zones={zones}
                enabledZones={enabledZones}
                onToggleZone={toggleZone}
              />
            ))}
          </div>
        </section>

        {/* Parámetros de cotización */}
        <section data-tour="merchant-config-quote-params">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            Parámetros de cotización
          </h2>
          <div className="border border-border rounded-lg p-4 space-y-4">
            <div>
              <Label htmlFor="validity" className="text-sm font-medium">
                Vigencia de cotización (horas)
              </Label>
              <p className="text-xs text-muted-foreground mb-1">
                Tiempo que tiene el cliente para aprobar la cotización antes de que expire
              </p>
              <Input
                id="validity"
                type="number"
                min="1"
                value={quoteValidityHours}
                onChange={(e) => setQuoteValidityHours(Number(e.target.value))}
                className="max-w-xs"
              />
            </div>

            <div className="flex gap-4">
              <div>
                <Label htmlFor="leadMin" className="text-sm font-medium">Plazo mínimo (días)</Label>
                <Input
                  id="leadMin"
                  type="number"
                  min="1"
                  value={leadTimeDaysMin}
                  onChange={(e) => setLeadTimeDaysMin(Number(e.target.value))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="leadMax" className="text-sm font-medium">Plazo máximo (días)</Label>
                <Input
                  id="leadMax"
                  type="number"
                  min="1"
                  value={leadTimeDaysMax}
                  onChange={(e) => setLeadTimeDaysMax(Number(e.target.value))}
                  className="mt-1"
                />
              </div>
            </div>
          </div>
        </section>

        <Button onClick={handleSave} disabled={saving}>
          {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          Guardar configuración
        </Button>

      </div>
    </ClinicalPageShell>
    </div>
  );
}
